/**
 * MONSTER TOWN — WHOSE FACE IS ON THE CARD. ERRAND CREW.
 *
 * LAW #4: an errand card cannot say "the zookeeper wants you". It has to SHOW
 * the zookeeper. That means every `giver` id in `src/content/errands.ts` needs a
 * face, and it needs the SAME face every time — a card whose monster changes
 * colour between sessions is a different monster to a three-year-old.
 *
 * Crews never import each other, so this file does not reach into the NPC
 * roster. It derives a stable `MonsterConfig` from the giver id itself with a
 * cheap string hash, which gives:
 *   - a real, distinct, cel-shaded monster (the same one the portrait renderer
 *     draws for the kid's own monster — one art system, not two);
 *   - determinism across reloads, save slots and machines;
 *   - no dependency on content that has not landed yet, so an errand naming
 *     `npc_zookeeper` looks right the day before the zoo crew ships.
 *
 * When the NPC crew does ship, they emit `errand:giverLook` once per neighbour
 * and the real face wins. That is the whole integration.
 */

import { bus } from '../core/events'
import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import type {
  MonsterConfig,
  MonsterBodyShape,
  MonsterEyes,
  MonsterHorns,
  MonsterPattern,
} from '../core/types'
import './events'

const SHAPES: readonly MonsterBodyShape[] = ['blob', 'tall', 'round', 'chunky']
const EYES: readonly MonsterEyes[] = ['two', 'one', 'three', 'sleepy']
const HORNS: readonly MonsterHorns[] = ['nubs', 'curved', 'antlers', 'antennae', 'none']
const PATTERNS: readonly MonsterPattern[] = ['belly', 'spots', 'stripes', 'star', 'plain']
const ACCENTS: readonly PaletteName[] = [
  'monsterCream',
  'monsterBelly',
  'flowerPink',
  'flowerYellow',
  'flowerWhite',
  'wallBlue',
]

/** FNV-1a. Small, stable, and the same number in every browser. */
function hash(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

function pick<T>(list: readonly T[], seed: number, fallback: T): T {
  if (list.length === 0) return fallback
  return list[seed % list.length] ?? fallback
}

/** Faces supplied by whoever actually owns the neighbour. Always wins. */
const overrides = new Map<string, MonsterConfig>()

export function setGiverLook(giver: string, look: MonsterConfig): void {
  if (giver === '') return
  overrides.set(giver, look)
}

/** The face for a giver id. Total — every string gets a monster. */
export function giverLook(giver: string): MonsterConfig {
  const override = overrides.get(giver)
  if (override !== undefined) return override
  const h = hash(giver === '' ? 'someone' : giver)
  return {
    shape: pick(SHAPES, h, 'blob'),
    color: pick(MONSTER_BODY_COLORS, h >>> 3, 'monsterTeal'),
    accentColor: pick(ACCENTS, h >>> 7, 'monsterCream'),
    eyes: pick(EYES, h >>> 11, 'two'),
    horns: pick(HORNS, h >>> 15, 'nubs'),
    pattern: pick(PATTERNS, h >>> 19, 'belly'),
    hat: null,
    outfit: null,
  }
}

let wired = false

/** Listens for the NPC crew's real faces. Idempotent. */
export function installGivers(): void {
  if (wired) return
  wired = true
  bus.on('errand:giverLook', (e) => setGiverLook(e.giver, e.look))
}
